import { useEffect, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import axiosNest from '../api/axiosNest';
import { fmtDate } from '../shared/Panel';
import { plural } from '../shared/plural';

/**
 * Stan synchronizacji kartotek z TMS klienta (W12: „kiedy ostatnio pobrane,
 * z jakim wynikiem; błąd ostatniego przebiegu nie jest cichy").
 *
 * Źródło: dziennik przebiegów importu (`GET /tms-import/latest`). Liczy się
 * wyłącznie przebieg Z ZAPISEM — suchy bieg niczego nie zmienia, więc nie
 * jest synchronizacją. Harmonogram importu jest domyślnie wyłączony (decyzja
 * PM, patrz `README-import.md`) — pasek mówi wprost, czy dane odświeża
 * automat, czy trzeba je odświeżać ręcznie, żeby stara synchronizacja nie
 * wyglądała na awarię.
 */

export interface ResourceReportUi {
  pobrano?: number;
  utworzono?: number;
  zaktualizowano?: number;
  odrzucono?: string[];
  ukonczone?: boolean;
}

export interface ImportRun {
  id: number;
  startedAt: string;
  finishedAt: string | null;
  dryRun: boolean;
  completed: boolean;
  error: string | null;
  report: {
    zasoby?: Record<string, ResourceReportUi>;
    ostrzezenia?: string[];
  } | null;
}

export const LAST_RUN_URL = '/tms-import/latest';

export interface TmsSchedule {
  enabled: boolean;
  everyMin: number | null;
}

/**
 * Jedno zdanie o harmonogramie — brak informacji nie jest „wyłączony".
 *
 * Bez „import uruchamia operator” (poprawa brzmienia 01.10.2026): planista
 * nie ma przycisku importu, a zdanie sugerowało, że to jego zadanie. Pasek
 * mówi tylko, czy dane odświeżają się same.
 */
export function describeSchedule(h: TmsSchedule | undefined): string | null {
  if (!h) return null;
  return h.enabled && h.everyMin !== null
    ? `automatyczne odświeżanie co ${h.everyMin} min`
    : 'automatyczne odświeżanie wyłączone';
}

/**
 * Po ilu godzinach synchronizacja jest „stara". Nasze założenie — bez
 * harmonogramu importu próg tylko koloruje komunikat, niczego nie wymusza.
 */
export const FRESHNESS_HOURS = 24;

/**
 * Rodzaj gramatyczny nazwy kroku — od niego zależy forma przy liczbie:
 * „1 nowy” (pojazd), „1 nowa” (nieobecność), „1 nowe” (zlecenie),
 * a przy osobach „2 nowych” (kierowców), nie „2 nowe”.
 */
type StepGender = 'm' | 'mp' | 'f' | 'n';

/**
 * Kroki importu po polsku. Klucze to nazwy kroków zapisane w raporcie
 * przebiegu (`report.zasoby`, JSON w bazie — `apps/api/src/via/
 * tms-import.service.ts`), więc ich nie tłumaczymy; tłumaczymy wyświetlanie.
 * Wcześniej mapa miała klucze opcji importu (`dowiazania`,
 * `kontrahenciZeZlecen`) zamiast nazw z raportu i na pasku wychodziły surowe
 * „nieobecnosci” czy „uprawnieniaPojazdow” (poprawa brzmienia 01.10.2026).
 * Komplet pilnuje `SyncStatus.test.tsx`, czytając serwis importu z dysku.
 */
export const IMPORT_STEP_LABELS: Record<string, { label: string; gender: StepGender }> = {
  systemyPracy: { label: 'systemy pracy', gender: 'm' },
  pojazdy: { label: 'pojazdy', gender: 'm' },
  pojazdyZmiany: { label: 'pojazdy zmienione w TMS', gender: 'm' },
  kierowcy: { label: 'kierowcy', gender: 'mp' },
  zlecenia: { label: 'zlecenia', gender: 'n' },
  nieobecnosci: { label: 'nieobecności kierowców', gender: 'f' },
  kontrahenci: { label: 'kontrahenci', gender: 'mp' },
  'dowiązania': { label: 'powiązania zleceń', gender: 'n' },
  uprawnieniaPojazdow: { label: 'uprawnienia pojazdów', gender: 'n' },
  uprawnienia: { label: 'uprawnienia kierowców', gender: 'n' },
  zleceniaSerwisowe: { label: 'zlecenia serwisowe', gender: 'n' },
  anulacjeTms: { label: 'anulowane zlecenia z TMS', gender: 'n' },
};

/** Liczba z przymiotnikiem w formie zgodnej z rodzajem: „41 nowych”, „1 nowa”. */
function countWith(n: number, stem: string, gender: StepGender): string {
  const one = `${stem}${gender === 'f' ? 'a' : gender === 'n' ? 'e' : 'y'}`;
  const few = gender === 'mp' ? `${stem}ych` : `${stem}e`;
  return plural(n, one, few, `${stem}ych`);
}

/**
 * Jedno zdanie o wyniku: co doszło, co się zmieniło, co odrzucono —
 * słowami („zlecenia: 41 nowych, 1781 zaktualizowanych”), nie skrótami
 * „+41, ~1781” (poprawa brzmienia 01.10.2026).
 */
export function describeOutcome(p: ImportRun): string {
  if (p.error) return `przerwana z powodu błędu: ${p.error}`;
  const parts = Object.entries(p.report?.zasoby ?? {}).map(([resourceKey, z]) => {
    const step = IMPORT_STEP_LABELS[resourceKey];
    const gender = step?.gender ?? 'n';
    const pieces = [
      z.utworzono ? countWith(z.utworzono, 'now', gender) : null,
      z.zaktualizowano ? countWith(z.zaktualizowano, 'zaktualizowan', gender) : null,
      z.odrzucono?.length ? countWith(z.odrzucono.length, 'odrzucon', gender) : null,
      z.ukonczone === false ? 'przerwano' : null,
    ].filter((k): k is string => k !== null);
    return `${step?.label ?? resourceKey}: ${pieces.length ? pieces.join(', ') : 'bez zmian'}`;
  });
  return parts.length ? parts.join(' · ') : 'bez zmian';
}

export function isStale(p: ImportRun, now = new Date()): boolean {
  const finishedMs = new Date(p.finishedAt ?? p.startedAt).getTime();
  return now.getTime() - finishedMs > FRESHNESS_HOURS * 3600_000;
}

/**
 * Wiadomość paska nie może rozpychać strony: długie zdania z raportu
 * (ostrzeżenia importu bywają na 200+ znaków) łamią się w obrębie okna.
 * Bez `minWidth: 0` element flex w `Alert` rośnie do szerokości tekstu.
 */
const ALERT_SX = {
  mb: 1,
  '& .MuiAlert-message': { minWidth: 0, overflowWrap: 'anywhere' },
} as const;

/**
 * Uwagi z raportu importu — zwinięte, bo to kontekst, nie stan: stan
 * (data, wynik, błąd) jest w pierwszej linii i zawsze widoczny.
 */
const ImportWarnings = ({ warnings }: { warnings: string[] }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Button
        size="small"
        onClick={() => setOpen((v) => !v)}
        sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
      >
        {open ? 'Ukryj uwagi' : `Uwagi z synchronizacji (${warnings.length})`}
      </Button>
      {open ? (
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {warnings.map((w, i) => (
            <Typography key={i} component="li" variant="caption" color="text.secondary">
              {w}
            </Typography>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

export const SyncStatus = () => {
  // `undefined` = jeszcze nie wiemy; `null` = nigdy nie synchronizowano.
  const [run, setRun] = useState<ImportRun | null | undefined>(
    undefined,
  );
  const [schedule, setSchedule] = useState<TmsSchedule | undefined>();

  useEffect(() => {
    let active = true;
    axiosNest
      .get<{ run?: ImportRun | null; schedule?: TmsSchedule }>(
        LAST_RUN_URL,
      )
      .then((r) => {
        if (!active) return;
        setRun(r.data?.run ?? null);
        setSchedule(r.data?.schedule);
      })
      .catch(() => {
        // Błąd pobrania to brak wiedzy, nie „nigdy nie synchronizowano" —
        // nic nie pokazujemy, zamiast twierdzić coś, czego nie wiemy.
        if (active) setRun(undefined);
      });
    return () => {
      active = false;
    };
  }, []);

  if (run === undefined) return null;

  if (run === null) {
    const scheduleText = describeSchedule(schedule);
    return (
      <Alert severity="info" sx={ALERT_SX}>
        Dane nie były jeszcze synchronizowane z TMS. Widoczne dane wczytano
        jednorazowo przy wdrożeniu systemu.
        {scheduleText ? ` ${scheduleText.charAt(0).toUpperCase()}${scheduleText.slice(1)}.` : ''}
      </Alert>
    );
  }

  const inProgress = run.finishedAt === null;
  const severity = run.error
    ? 'error'
    : inProgress
      ? 'info'
      : !run.completed || isStale(run)
        ? 'warning'
        : 'success';

  return (
    <Alert severity={severity} sx={ALERT_SX}>
      <Typography variant="body2">
        Ostatnia synchronizacja z TMS: {fmtDate(run.startedAt)}
        {inProgress ? ' — w toku' : ` — ${describeOutcome(run)}`}
        {!inProgress && !run.error && isStale(run)
          ? ` (starsza niż ${FRESHNESS_HOURS} h)`
          : ''}
        {describeSchedule(schedule) ? ` · ${describeSchedule(schedule)}` : ''}
      </Typography>
      {run.report?.ostrzezenia?.length ? (
        <ImportWarnings warnings={run.report.ostrzezenia} />
      ) : null}
    </Alert>
  );
};
